<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useLocalizationStore } from '@/stores/localization.ts'
import { usePreferencesStore, type OfflineImageTier } from '@/stores/preferences.ts'
import { useCatalogSyncStore } from '@/stores/catalog-sync'
import { useAuthStore } from '@/stores/auth.ts'
import { useUiStore } from '@/stores/ui.store.ts'
import { sendError } from '@/utils/sendError.ts'
import { showToast } from '@/utils/showToast'
import GeneralDialog from '@/components/GeneralDialog.vue'
import Button from '@/components/Button.vue'
import IconCustom from '@/components/IconCustom.vue'
import $typo from '@/styles/typografy.module.css'

const CATALOG_DOWNLOAD_SIZE = '~170 MB'
const CATALOG_WITH_COVERS_DOWNLOAD_SIZE = '~300 MB'
const COVERS_TIER: OfflineImageTier = '192'
const OFFLINE_DOWNLOAD_LOADER = 'offline-download'

type OfflineChoice = 'none' | typeof COVERS_TIER

const { t } = useLocalizationStore()
const { offlineModeEnabled } = storeToRefs(usePreferencesStore())
const { setOfflineImageTier, setOfflineModeEnabled } = usePreferencesStore()
const { canUseOffline } = storeToRefs(useCatalogSyncStore())
const { enableOfflineMode } = useCatalogSyncStore()
const { authState } = storeToRefs(useAuthStore())
const { startLoading, stopLoading } = useUiStore()
const route = useRoute()

const shouldPrompt = computed(
  () =>
    canUseOffline.value &&
    authState.value.sessionExists &&
    offlineModeEnabled.value === null &&
    !route.path.startsWith('/auth'),
)

const open = ref(false)
const selectedChoice = ref<OfflineChoice | null>(null)

const PROMPT_DELAY_MS = 1500

watch(
  shouldPrompt,
  (prompt, _previousPrompt, onCleanup) => {
    if (!prompt || open.value) return
    const timer = setTimeout(() => {
      open.value = true
    }, PROMPT_DELAY_MS)
    onCleanup(() => clearTimeout(timer))
  },
  { immediate: true },
)

const choices = computed<{ value: OfflineChoice; label: string; size?: string }[]>(() => [
  { value: 'none', label: t('settingsOfflineConsentOffline'), size: CATALOG_DOWNLOAD_SIZE },
  {
    value: COVERS_TIER,
    label: t('settingsOfflineConsentOfflineCovers'),
    size: CATALOG_WITH_COVERS_DOWNLOAD_SIZE,
  },
])

async function cancel() {
  open.value = false
  await setOfflineModeEnabled(false)
}

async function save() {
  const choice = selectedChoice.value
  if (choice === null) return
  open.value = false
  await startDownload(choice)
}

async function startDownload(tier: OfflineImageTier) {
  showToast({ text: t('settingsOfflineDownloadingToast'), duration: 'short' })
  startLoading(OFFLINE_DOWNLOAD_LOADER)
  try {
    await setOfflineImageTier(tier)
    await enableOfflineMode()
  } catch (error) {
    sendError(error, { context: 'Offline-mode enable failed' })
  } finally {
    stopLoading(OFFLINE_DOWNLOAD_LOADER)
  }
}
</script>

<template>
  <GeneralDialog v-model:visible="open" :close-on-backdrop-click="false">
    <div class="p-5 flex flex-col gap-4">
      <h2 :class="$typo.openSansBold16" class="text-text">
        {{ t('settingsOfflineConsentTitle') }}
      </h2>
      <p :class="$typo.openSans14" class="text-text-muted leading-5">
        {{ t('settingsOfflineConsentMessage') }}
      </p>

      <div class="flex flex-col gap-2.5">
        <button
          v-for="choice in choices"
          :key="choice.value"
          type="button"
          class="w-full rounded-xl px-4 py-3 flex items-center justify-between border outline-none"
          :class="
            selectedChoice === choice.value
              ? 'bg-primary/10 border-primary'
              : 'bg-button border-transparent'
          "
          @click="selectedChoice = choice.value"
        >
          <span class="flex items-center gap-2.5">
            <span
              class="w-5 h-5 rounded-full flex items-center justify-center border-2 shrink-0"
              :class="
                selectedChoice === choice.value ? 'bg-primary border-primary' : 'border-text-muted'
              "
            >
              <IconCustom
                v-if="selectedChoice === choice.value"
                ligature="check"
                size="12"
                class="text-white"
              />
            </span>
            <span :class="$typo.openSans14" class="text-text font-bold">{{ choice.label }}</span>
          </span>
          <span v-if="choice.size" :class="$typo.openSans12" class="text-text-muted">
            {{ choice.size }}
          </span>
        </button>
      </div>

      <div class="flex justify-between mt-2">
        <Button :class="$typo.openSans12" @click="cancel">
          <IconCustom ligature="close" size="16" />
          {{ t('settingsOfflineConsentDecline') }}
        </Button>
        <Button :class="$typo.openSans12" :disabled="selectedChoice === null" @click="save">
          <IconCustom ligature="check" size="16" />
          {{ t('optionsModalSave') }}
        </Button>
      </div>
    </div>
  </GeneralDialog>
</template>
